import { useEffect, useMemo, useRef } from 'react'
import { connect } from 'react-redux'
import {
  buildMiniSearchIndices,
  searchPerMiniSearchIndex,
} from '../local/buildMiniSearchIndices'

/**
 * Headless shadow search provider for local Redux-derived library content,
 * powered by MiniSearch (BM25 + token-level prefix/fuzzy).
 *
 * Sibling of `FuseContentSearchProvider`. Both run as shadows alongside the
 * Algolia display provider; results feed searchReporter under
 * providerId='minisearch-content' (not shown to the user). The intent is to
 * evaluate BM25-style scoring against Fuse's Bitap algorithm directly against
 * Algolia at runtime.
 *
 * Output shape mirrors AlgoliaSearchProvider and FuseContentSearchProvider
 * exactly so the same `IndexHitList` / `SectionHeader` primitives can
 * render any of the three engines without branching:
 *   {
 *     suggestions: [{ index, hits }, ...],
 *     hits:        <flat list across all indices>,
 *     indexResults: {
 *       repos:          { hits, hitCount, hasMore, loadMore },
 *       folders:        { hits, hitCount, hasMore, loadMore },
 *       sets_questions: { hits, hitCount, hasMore, loadMore },
 *     }
 *   }
 *
 * Each hit copies the source Redux record and adds:
 *   - objectID:           Algolia-compatible primary key (= record id)
 *   - __localOrigin:      true marker so the UI can render a local badge
 *   - __localEngine:      'minisearch' — distinguishes from Fuse hits
 *   - __minisearchScore:  BM25 score (higher = stronger match, opposite of Fuse)
 *   - __minisearchTerms:  array of query terms that matched
 *   - __minisearchMatch:  per-term → matched-fields map (MiniSearch `match`)
 */
function MiniSearchContentProvider({
  query, options, onResults,
  sets, questions, folders, repos,
}) {
  const indices = useMemo(
    () => buildMiniSearchIndices({
      sets, questions, folders, repos,
    }),
    [sets, questions, folders, repos],
  )

  const onResultsRef = useRef(onResults)
  onResultsRef.current = onResults

  useEffect(() => {
    if (!query) {
      onResultsRef.current({
        suggestions: [],
        hits: [],
        indexResults: undefined,
      })
      return
    }
    const limit = options && options.hitsPerPage ? options.hitsPerPage : 10
    const indexResults = searchPerMiniSearchIndex(indices, query, limit)
    const flatHits = [
      ...indexResults.repos.hits,
      ...indexResults.folders.hits,
      ...indexResults.sets_questions.hits,
    ]
    const suggestions = [
      { index: 'repos', hits: indexResults.repos.hits },
      { index: 'folders', hits: indexResults.folders.hits },
      { index: 'sets_questions', hits: indexResults.sets_questions.hits },
    ]
    onResultsRef.current({
      suggestions,
      hits: flatHits,
      indexResults,
    })
    // eslint-disable-next-line no-console
    console.log('[local-search:minisearch-content]', {
      query,
      limit,
      hitCount: flatHits.length,
      indexResults,
      hits: flatHits,
    })
  }, [query, indices, options])

  return null
}

function mapStateToProps(state) {
  return {
    sets: state.sets,
    questions: state.questions,
    folders: state.folders,
    repos: state.repos,
  }
}

export default connect(mapStateToProps)(MiniSearchContentProvider)
